import React, { useState, useEffect, useMemo } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  IconButton
} from '@material-ui/core';
import NumberFormat from 'react-number-format';
import { editTxnOpp } from 'store/actions';
import ClearIcon from '@material-ui/icons/Clear';

const toString = val => { // Convert to string with null/undefined handling
  if (val == null) return '';
  return val.toString();
}

const EditOpportunityModal = ({ classes, open, handleClose, selectedOpportunity, editTxnOpp, selectedContainer }) => {
  const initialFieldsValues = useMemo(() => {
    return {
      beds: toString(selectedOpportunity.beds),
      baths: toString(selectedOpportunity.baths),
      yearBuilt: toString(selectedOpportunity.yearBuilt),
      sqft: toString(selectedOpportunity.sqft),
      askingPrice: toString(selectedOpportunity.askingPrice),
      opportunitySource: toString(selectedOpportunity.opportunitySource)
    }
  }, [selectedOpportunity]);
  const [fields, setFields] = useState(initialFieldsValues);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (open) { // Reset values on open
      setFields(initialFieldsValues);
    }
  }, [open, initialFieldsValues]);

  useEffect(() => {
    if (selectedOpportunity.isProcessing && open) { // Close modal if opportunity begins processing
      handleClose();
    }
  }, [selectedOpportunity, open]);

  const handleFieldChange = (value, field) => {
    if (validationErrors[field]) {
      const newErrs = {...validationErrors};
      delete newErrs[field];
      setValidationErrors(newErrs);
    }
    setFields({
      ...fields,
      [field]: value
    })
  }

  const trimValue = field => {
    const trimmed = fields[field].trim();
    if (fields[field] !== trimmed) {
      handleFieldChange(trimmed, field);
    }
  }
  
  const handleEdit = () => {
    if (isSubmittingEdit) return;
    setIsSubmittingEdit(true);
    editTxnOpp({
      transactionOpportunityId: selectedOpportunity.id,
      ...fields
    }, handleEditRes);
  }

  const handleEditRes = (res) => {
    setIsSubmittingEdit(false);
    if (!res) return; // Edit request returned error
    if (res.passedValidation) {
      handleClose(); // Edit request succeeded with no errors
    }

    const errObj = {};
    res.validationErrors.forEach(err => {
      errObj[err.fieldName] = err.errorMessage;
    });
    setValidationErrors(errObj);
  }

  const onClose = (e, reason) => {
    if (reason === 'backdropClick') return; // updated handling for disableBackdropClick
    handleClose();
  }

  const isNullorEmptyString = val => {
    return val == null || val === '';
  }
  const someValueChanged = Object.keys(initialFieldsValues).some(key => initialFieldsValues[key] !== fields[key].trim());
  const someRequiredFieldIsEmpty = ['beds', 'baths', 'yearBuilt', 'sqft', 'askingPrice'].some(field => isNullorEmptyString(fields[field]));

  return (
    <Dialog onClose={onClose} open={open} className={classes.dialog}>
      <DialogTitle>
        {`Edit Opportunity ${selectedOpportunity.id}`}
      </DialogTitle>
      <IconButton className={classes.closeButton} onClick={handleClose}>
        <ClearIcon fontSize="inherit" />
      </IconButton>

      {
        selectedContainer.viewingReadonlyMode &&
        <>
          <DialogContent>Cannot edit while in read-only mode</DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
          </DialogActions>
        </>
      }

      {
        !selectedContainer.viewingReadonlyMode &&
        <>
              <DialogContent>
        <div style={{display: 'flex'}}>
          <NumberFormat
            label='Beds'
            decimalScale={0}
            allowNegative={false}
            isAllowed={({ value, floatValue }) => {
              return !value || floatValue < 100
            }}
            onValueChange={values => handleFieldChange(values.value, 'beds')}
            value={fields.beds}
            customInput={TextField}
            className={classes.textFieldRoot}
            error={isNullorEmptyString(fields.beds) || !!validationErrors.beds}
            helperText={isNullorEmptyString(fields.beds) ? 'Beds is required' : validationErrors.beds ?? ''}
            FormHelperTextProps={{ classes: { error: classes.errorText }}}
            onBlur={() => trimValue('beds')}
          />
          <NumberFormat
            label='Baths'
            decimalScale={1}
            allowNegative={false}
            isAllowed={({ value, floatValue }) => {
              return !value || floatValue < 100
            }}
            onValueChange={values => handleFieldChange(values.value, 'baths')}
            value={fields.baths}
            customInput={TextField}
            className={classes.textFieldRoot}
            error={isNullorEmptyString(fields.baths) || !!validationErrors.baths}
            helperText={isNullorEmptyString(fields.baths) ? 'Baths is required' : validationErrors.baths ?? ''}
            FormHelperTextProps={{ classes: { error: classes.errorText }}}
            onBlur={() => trimValue('baths')}
          />
          <NumberFormat
            label='Year Built'
            decimalScale={0}
            allowNegative={false}
            format='####'
            onValueChange={values => handleFieldChange(values.value, 'yearBuilt')}
            value={fields.yearBuilt}
            customInput={TextField}
            className={classes.textFieldRoot}
            error={isNullorEmptyString(fields.yearBuilt) || !!validationErrors.yearBuilt}
            helperText={isNullorEmptyString(fields.yearBuilt) ? 'Year Built is required' : validationErrors.yearBuilt ?? ''}
            FormHelperTextProps={{ classes: { error: classes.errorText }}}
            onBlur={() => trimValue('yearBuilt')}
          />
          <NumberFormat
            label='Square Feet'
            thousandSeparator={true}
            allowNegative={false}
            isAllowed={({ value }) => {
              return !value || value.length <= 8
            }}
            decimalScale={0}
            onValueChange={values =>handleFieldChange(values.value, 'sqft')}
            value={fields.sqft}
            customInput={TextField}
            className={classes.textFieldRoot}
            error={isNullorEmptyString(fields.sqft) || !!validationErrors.sqft}
            helperText={isNullorEmptyString(fields.sqft) ? 'Square Feet is required' : validationErrors.sqft ?? ''}
            FormHelperTextProps={{ classes: { error: classes.errorText }}}
            onBlur={() => trimValue('sqft')}
          />
        </div>
        <div style={{display: 'flex'}}>
          <NumberFormat
            label='Asking Price'
            thousandSeparator={true}
            allowNegative={false}
            isAllowed={({ value }) => {
              return !value || value.length <= 8
            }}
            decimalScale={0}
            onValueChange={values =>handleFieldChange(values.value, 'askingPrice')}
            value={fields.askingPrice}
            customInput={TextField}
            prefix={'$'}
            className={classes.textFieldRoot}
            error={isNullorEmptyString(fields.askingPrice) || !!validationErrors.askingPrice}
            helperText={isNullorEmptyString(fields.askingPrice) ? 'Asking Price is required' : validationErrors.askingPrice ?? ''}
            FormHelperTextProps={{ classes: { error: classes.errorText }}}
            onBlur={() => trimValue('askingPrice')}
          />
          <TextField
            label='Opportunity Source'
            value={fields.opportunitySource}
            onChange={e =>handleFieldChange(e.target.value, 'opportunitySource')}
            className={classes.textFieldRoot}
            error={!!validationErrors.opportunitySource || fields.opportunitySource?.length > 100}
            helperText={validationErrors.opportunitySource ?? fields.opportunitySource?.length > 100 ? '100 characters max' : ''}
            FormHelperTextProps={{ classes: { error: classes.errorText }}}
            onBlur={() => trimValue('opportunitySource')}
            style={{ flexBasis: '75%' }}
            disabled={true}
          />
        </div>
        {/* <DialogContentText id="alert-dialog-description"> */}
        <Typography>
          Editing the transaction opportunity will also archive existing data and pull latest information.
        </Typography>
        {/* </DialogContentText> */}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleEdit}
          variant="contained"
          color="primary"
          disabled={!someValueChanged || someRequiredFieldIsEmpty || fields.opportunitySource?.length > 100 || selectedOpportunity.isProcessing}
          style={{ width: 68 }}
        >
          {isSubmittingEdit ? <CircularProgress size={24} color='inherit'/> : 'Save'}
        </Button>
      </DialogActions>
        </>
      }


    </Dialog>
  )
}

const styles = theme => ({
  dialog: {
    minWidth: '200px',
    minHeight: '150px'
  },
  textFieldRoot: {
    height: 70,
    marginBottom: 16,
    paddingRight: 8,
    flexBasis: '25%'
  },
  errorText: {
    lineHeight: 1
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
  },
});

const mapState = state => ({
  selectedOpportunity: state.propertyUWState.propertyUWClientTxnOpp,
  selectedContainer: state.propertyUWState.selectedContainer,
  confirmationModalState: state.appState.confirmationModalState,
});

const mapDispatch = {
  editTxnOpp
}

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(EditOpportunityModal);